import {
  Injectable,
} from '@angular/core';
import { chain, find, map } from 'lodash-es';
import { Observable } from 'rxjs';
import { map as rxjsmap } from 'rxjs/operators';

import { VersionService } from 'webcommon/common-api';
import {
  ConfigurationRepository,
  DeviceSettingsRepository,
  ServerInfoRepository,
  WebCommonStorageRepository,
} from 'webcommon/legacy-common';
import {
  PatientDashboardConfigurationV1,
  PatientDashboardConfigurationV2Dto,
  TileV2Dto,
  WidgetDescriptorV1Dto,
} from 'webcommon/shared';
import {
  SettingRepository,
} from 'webcommon/webapi';

const widgetViews = [
  {
    id: '259e6a7b-64a8-437c-82b7-000000000001',
    name: 'allergies',
    displayName: 'Allergies',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/allergiesTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000002',
    name: 'appointments',
    displayName: 'Appointments',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/appointmentsTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000003',
    name: 'demographics',
    displayName: 'Demographics',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/demographicsTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000004',
    name: 'header',
    displayName: 'Patient Header',
    userPlaceable : false,
    view: '/patients/views/dashboard-widgets/patientHeaderTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000009',
    name: 'problems',
    displayName: 'Problem List',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/problemListTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000006',
    name: 'medications',
    displayName: 'Active Medications',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/medicationsTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000012',
    name: 'visits',
    displayName: 'Visits',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/visitsTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000010',
    name: 'remarks',
    displayName: 'Remarks',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/remarksTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000008',
    name: 'orders',
    displayName: 'Orders',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/ordersTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000007',
    name: 'observationResults',
    displayName: 'Observation Results',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/observationResultsTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000011',
    name: 'vitals',
    displayName: 'Vitals',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/vitalsTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000005',
    name: 'healthMaintenanceReminders',
    displayName: 'Health Reminders',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/healthMaintenanceRemindersTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000013',
    name: 'attachments',
    displayName: 'Attachments',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/attachmentsTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  {
    id: '259e6a7b-64a8-437c-82b7-000000000014',
    name: 'observationValues',
    displayName: 'Observation Values',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/observationValuesTile.html',
    configurationView: '/configuration/views/observation-values-widget-configuration.html',
  },
  /* removed until feature fully implemented for hardcoded values like A1C
  {
    id: '259e6a7b-64a8-437c-82b7-000000000015',
    name: 'diagnosisSummaries',
    displayName: 'Diagnosis Summaries',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/diagnosisSummariesTiles.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  },
  */
  {
    id: '259e6a7b-64a8-437c-82b7-000000000016',
    name: 'recalls',
    displayName: 'Recalls',
    userPlaceable : true,
    view: '/patients/views/dashboard-widgets/recallsTile.html',
    configurationView: '/configuration/views/default-widget-configuration.html',
  }
];

@Injectable({
  providedIn: 'root',
})
export class DashboardStructureService {
  static readonly ajsFactoryName = 'DashboardStructure';

  constructor(
    private configurationRepository: ConfigurationRepository,
    private deviceSettingsRepository: DeviceSettingsRepository,
    private serverInfoRepository: ServerInfoRepository,
    private settingRepository: SettingRepository,
    private versionService: VersionService,
    private webCommonStorageRepository: WebCommonStorageRepository,
  ) {
  }

  private mapV1ConfigurationsToV2(configs: PatientDashboardConfigurationV1[]): PatientDashboardConfigurationV2Dto[] {
    const resultList = map(configs, config => this.mapV1ConfigurationToV2(config));

    return resultList;
  }

  private mapV1ConfigurationToV2(config: PatientDashboardConfigurationV1): PatientDashboardConfigurationV2Dto {
    const result: PatientDashboardConfigurationV2Dto = {
      Id: config.Type,
      ExternalId: '',
      Name: config.Type,
      DisplayOrder: config.Type === 'All' ? 0 : 1,
      PhoneTiles: this.mapTiles(config, 1),
      TabletTiles: this.mapTiles(config, 2),
      DesktopTiles: this.mapTiles(config, 3),
    };

    return result;
  }

  private mapTiles(config: PatientDashboardConfigurationV1, numColumns: number) {
    const tiles = chain(config.WidgetDescriptors)
      .filter(tile => {
        return tile.Name !== 'header';
      })
      .map((tile) => {
        return this.mapTile(tile, numColumns);
      })
      .value();

    return tiles;
  }

  private mapTile(tile: WidgetDescriptorV1Dto, numColumns: number): TileV2Dto {
    const widgetView = find(widgetViews, {name: tile.Name});
    if (!widgetView) {
      throw new Error(`Unknown widget name: ${tile.Name}`);
    }

    const result: TileV2Dto = {
      Id: tile.Id.toString(),
      TileDescriptorId: widgetView.id,
      Name: tile.Name,
      Heading: tile.Heading,
      ColumnSpan: Math.min(this.deviceSettingsRepository.isPhoneOrOverride || tile.Column !== 'dblCol' ? 1 : 2, numColumns || 2),
      IsCollapsed: tile.Name === 'remarks' ? false : true,
      Filter: tile.Filter,
    };

    return result;
  }

  private getV1(): Observable<PatientDashboardConfigurationV2Dto[]> {
    const userId = this.webCommonStorageRepository.getUserIdOrThrow();
    return this.settingRepository.getPatientDashboardStructureV1(userId).pipe(
      rxjsmap(list => this.mapV1ConfigurationsToV2(list)),
    );
  }

  private getV2(): Observable<PatientDashboardConfigurationV2Dto[]> {
    return this.settingRepository.getPatientDashboardStructureV2();
  }

  get(): Observable<PatientDashboardConfigurationV2Dto[]> {
    const isTileV2 = this.versionService.checkVersionValid(
      this.serverInfoRepository.getPrmAssemblyVersion() || '',
      this.configurationRepository.MIN_PRM_DASHBOARD_VERSION || '',
    );

    if (isTileV2) {
      return this.getV2();
    } else {
      return this.getV1();
    }
  }

  getWidgetViews() {
    return widgetViews;
  }
}
